type RouteParams = { [key: string]: string };
type QueryParams = { [key: string]: string | string[] };
type RouteCallback = (params: RouteParams, query: QueryParams) => void;

interface Route {
    pattern: string;
    callback: RouteCallback;
}

export class Router {
    private routes: Route[] = [];
    private defaultRoute: RouteCallback | null = null;

    constructor() {
        // Listen to hash changes
        window.addEventListener('hashchange', () => this.handleRoute());
        // Handle initial route
        window.addEventListener('load', () => this.handleRoute());
    }

    /**
     * Add a new route
     * @param pattern Route pattern (e.g., "/user/:id/profile")
     * @param callback Callback to execute when route matches
     */
    public on(pattern: string, callback: RouteCallback): void {
        this.routes.push({ pattern, callback });
    }

    /**
     * Set the default route handler
     * @param callback Callback to execute when no routes match
     */
    public default(callback: RouteCallback): void {
        this.defaultRoute = callback;
    }

    /**
     * Navigate to a new route
     * @param path Path to navigate to
     * @param query Optional query parameters
     */
    public navigate(path: string, query: QueryParams = {}): void {
        const queryString = this.buildQueryString(query);
        const hash = queryString ? `#${path}?${queryString}` : `#${path}`;
        window.location.hash = hash;
    }

    /**
     * Get current route parameters
     */
    public getParams(): RouteParams {
        const { pathname } = this.parseHash();
        const route = this.findMatchingRoute(pathname);
        return route ? this.extractRouteParams(pathname, route.pattern) : {};
    }

    public getPathName(): string {
        return this.parseHash().pathname;
    }

    /**
     * Get current query parameters
     */
    public getQuery(): QueryParams {
        const { search } = this.parseHash();
        return this.parseQueryString(search);
    }

    private handleRoute(): void {
        const { pathname, search } = this.parseHash();
        const route = this.findMatchingRoute(pathname);

        if (route) {
            const params = this.extractRouteParams(pathname, route.pattern);
            const query = this.parseQueryString(search);
            route.callback(params, query);
        } else if (this.defaultRoute) {
            this.defaultRoute({}, {});
        }
    }

    private parseHash(): { pathname: string; search: string } {
        const hash = window.location.hash.slice(1); // Remove #
        const [pathname, search] = hash.split('?');
        return { pathname: pathname || '/', search: search || '' };
    }

    private findMatchingRoute(pathname: string): Route | null {
        return this.routes.find(route => this.isRouteMatch(pathname, route.pattern)) || null;
    }

    private isRouteMatch(pathname: string, pattern: string): boolean {
        const patternParts = pattern.split('/');
        const pathParts = pathname.split('/');

        if (patternParts.length !== pathParts.length) {
            return false;
        }

        return patternParts.every((part, i) => {
            if (part.startsWith(':')) {
                return true; // Variable part always matches
            }
            return part === pathParts[i];
        });
    }

    private extractRouteParams(pathname: string, pattern: string): RouteParams {
        const params: RouteParams = {};
        const patternParts = pattern.split('/');
        const pathParts = pathname.split('/');

        patternParts.forEach((part, i) => {
            if (part.startsWith(':')) {
                const paramName = part.slice(1);
                params[paramName] = pathParts[i];
            }
        });

        return params;
    }

    private parseQueryString(search: string): QueryParams {
        const params: QueryParams = {};
        if (!search) return params;

        const searchParams = new URLSearchParams(search);
        searchParams.forEach((value, key) => {
            // Check if the value contains commas, if so split it into an array
            if (value.includes(',')) {
                params[key] = value.split(',');
            } else {
                params[key] = value;
            }
        });

        return params;
    }

    private buildQueryString(query: QueryParams): string {
        // Build the query string manually to avoid URLSearchParams encoding
        const parts: string[] = [];
        
        Object.entries(query).forEach(([key, value]) => {
            // Don't encode the key since it's a simple identifier
            if (Array.isArray(value)) {
                // Join array values with commas and don't encode the commas
                const encodedValues = value.map(v => encodeURIComponent(v)).join(',');
                parts.push(`${key}=${encodedValues}`);
            } else {
                // Only encode the value, not the key
                parts.push(`${key}=${encodeURIComponent(value)}`);
            }
        });

        return parts.join('&');
    }
}

// Create and export a singleton instance
export const router = new Router();

// Example usage:
/*
router.on('/user/:id', (params, query) => {
    console.log('User ID:', params.id);
    console.log('Query params:', query);
});

router.on('/search', (params, query) => {
    console.log('Search query:', query.q);
    console.log('Filters:', query.filters); // Will be an array if comma-separated
});

router.default((params, query) => {
    console.log('404 - Route not found');
});

// Navigate with parameters
router.navigate('/user/123', { tab: 'profile', view: 'details' });

// Navigate with array parameters - will be comma-separated in URL
router.navigate('/search', { 
    q: 'test',
    filters: ['price', 'date'] // Will appear as ?filters=price,date in URL
});
*/